import { useEffect, useState, useRef } from 'preact/hooks';
import { useAuth, UserButton } from '@clerk/clerk-react';
import { route } from 'preact-router';
import { api } from '../api/client';
import type { SessionFocus, SessionContractInfo, SessionSummaryData } from '../api/client';
import { SessionProgress } from '../components/SessionProgress';
import { ProfileNotes } from '../components/ProfileNotes';
import { SessionFocusPicker } from '../components/SessionFocusPicker';
import { ClaimReview } from '../components/ClaimReview';
import { InterviewSkeleton, SkeletonStyles } from '../components/Skeleton';

/** Render inline markdown (bold, italic) to HTML. Safe for controlled LLM output. */
function renderInlineMarkdown(text: string): string {
	return text
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
		.replace(/\*(.+?)\*/g, '<em>$1</em>');
}

interface Message {
	role: 'user' | 'assistant' | 'system';
	content: string;
	timestamp: number;
}

interface Claim {
	id: string;
	sessionId?: string;
	section: string;
	field: string;
	value: unknown;
	confidence: 'stated' | 'inferred';
	timestamp: number;
	agentReasoning?: string;
	narrative?: string;
}

interface Gap {
	id: string;
	section: string;
	field?: string;
	reason: string;
	priority: 'high' | 'medium' | 'low';
	status: 'pending' | 'explored' | 'resolved';
}

interface SessionData {
	id: string;
	type: string;
	mode?: 'canonical' | 'private';
	status: string;
	startedAt: number;
	lastActiveAt: number;
}

interface Props {
	sessionId?: string;
}


export function Interview({ sessionId }: Props) {
	const { getToken } = useAuth();
	const [session, setSession] = useState<SessionData | null>(null);
	const [messages, setMessages] = useState<Message[]>([]);
	const [pendingClaims, setPendingClaims] = useState<Claim[]>([]);
	const [approvedClaims, setApprovedClaims] = useState<Claim[]>([]);
	const [compiledView, setCompiledView] = useState<import('../components/ProfileNotes').CompiledProfileView | null>(null);
	const [gaps, setGaps] = useState<{ remaining: Gap[]; explored: Gap[]; resolved: Gap[] }>({
		remaining: [],
		explored: [],
		resolved: [],
	});
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(true);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showClaims, setShowClaims] = useState(true);
	const [profileDataReady, setBriefReady] = useState(false);
	const [dataBannerDismissed, setBriefDismissed] = useState(false);
	const [sessionFocus, setSessionFocus] = useState<SessionFocus | null>(null);
	const [settingFocus, setSettingFocus] = useState(false);
	const [contract, setContract] = useState<SessionContractInfo | null>(null);
	const [sessionSummary, setSessionSummary] = useState<SessionSummaryData | null>(null);
	const [completing, setCompleting] = useState(false);
	const [seedUrls, setSeedUrls] = useState('');
	const [showReview, setShowReview] = useState(false);
	const [recentClaimIds, setRecentClaimIds] = useState<Set<string>>(new Set());
	const [extractionNotice, setExtractionNotice] = useState<string | null>(null);
	// Track claims that belong to THIS session (extracted or loaded as pending/approved for this session)
	const sessionOwnedClaimIds = useRef(new Set<string>());

	// Session claim IDs for highlighting in ProfileNotes (claims from THIS session)
	const sessionClaimIds = new Set([
		...approvedClaims.map(c => c.id),
		...pendingClaims.map(c => c.id),
	]);

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const chatInputRef = useRef<HTMLDivElement>(null);
	const resizeState = useRef({ active: false, startY: 0, startH: 0 });

	useEffect(() => {
		if (sessionId) {
			loadSession(sessionId);
		} else {
			// No session ID - should redirect or start new
			route('/dashboard');
		}
	}, [sessionId]);

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	function scrollToBottom() {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}

	function getTextareaMaxHeight(): number {
		const container = chatInputRef.current;
		if (container && container.style.height) {
			// Container has been manually resized — calculate available space
			// Subtract padding (top: 0, bottom: 1rem=16px), resize handle (~20px), hint (~24px), gap
			const containerH = parseInt(container.style.height, 10);
			return Math.max(60, containerH - 70);
		}
		// Default: use viewport-relative max
		return Math.min(window.innerHeight * 0.4, 320);
	}

	function autoResizeTextarea() {
		const textarea = textareaRef.current;
		if (textarea) {
			textarea.style.overflow = 'hidden';
			textarea.style.height = 'auto';
			const maxHeight = getTextareaMaxHeight();
			textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
			textarea.style.overflow = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage(e);
		}
	}

	function onResizeStart(e: MouseEvent | TouchEvent) {
		e.preventDefault();
		const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
		const el = chatInputRef.current;
		if (!el) return;
		resizeState.current = { active: true, startY: clientY, startH: el.offsetHeight };
		el.classList.add('is-dragging');
	}

	useEffect(() => {
		function onResizeMove(e: MouseEvent | TouchEvent) {
			const rs = resizeState.current;
			if (!rs.active) return;
			const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
			// Dragging up = larger input area
			const delta = rs.startY - clientY;
			const newH = Math.max(100, Math.min(400, rs.startH + delta));
			const el = chatInputRef.current;
			if (el) el.style.height = newH + 'px';
			// Sync textarea to fill available space in resized container
			autoResizeTextarea();
		}
		function onResizeEnd() {
			if (!resizeState.current.active) return;
			resizeState.current.active = false;
			chatInputRef.current?.classList.remove('is-dragging');
		}
		document.addEventListener('mousemove', onResizeMove);
		document.addEventListener('touchmove', onResizeMove, { passive: false });
		document.addEventListener('mouseup', onResizeEnd);
		document.addEventListener('touchend', onResizeEnd);
		return () => {
			document.removeEventListener('mousemove', onResizeMove);
			document.removeEventListener('touchmove', onResizeMove);
			document.removeEventListener('mouseup', onResizeEnd);
			document.removeEventListener('touchend', onResizeEnd);
		};
	}, []);

	async function loadSession(id: string) {
		try {
			setLoading(true);
			const token = await getToken();
			if (!token) return;

			const data = await api.getSession(token, id);
			setSession(data.session);
			setMessages(data.messages);
			setPendingClaims(data.claims.pending);
			setApprovedClaims(data.claims.approved);
			// Track which claims belong to this session
			for (const c of data.claims.pending) sessionOwnedClaimIds.current.add(c.id);
			for (const c of data.claims.approved) sessionOwnedClaimIds.current.add(c.id);
			// Auto-show review for manual extraction sessions with pending claims
			if (data.session.type === 'manual_extraction' && data.claims.pending.length > 0 && data.messages.length === 0) {
				setShowReview(true);
			}
			if (data.compiledView) {
				setCompiledView(data.compiledView);
			}
			setGaps(data.gaps);
			if (data.session.sessionFocus) setSessionFocus(data.session.sessionFocus);
			if (data.contract) setContract(data.contract);
			if (data.sessionSummary) setSessionSummary(data.sessionSummary);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load session');
		} finally {
			setLoading(false);
		}
	}

	async function doSendMessage(userMessage: string, skipOptimistic?: boolean) {
		if (!userMessage || !sessionId) return;
		// When called from sendMessage, optimistic + sending already set
		if (!skipOptimistic && sending) return;

		if (!skipOptimistic) {
			setSending(true);
		}
		setError(null);

		// Optimistic update (skip if sendMessage already added it)
		let optimisticMessage: Message | null = null;
		if (!skipOptimistic) {
			optimisticMessage = {
				role: 'user',
				content: userMessage,
				timestamp: Date.now(),
			};
			setMessages(prev => [...prev, optimisticMessage!]);
		}

		try {
			const token = await getToken();
			if (!token) return;

			const response = await api.sendMessage(token, {
				sessionId,
				message: userMessage,
			});

			// Add assistant response
			const assistantMessage: Message = {
				role: 'assistant',
				content: response.message,
				timestamp: Date.now(),
			};
			setMessages(prev => [...prev, assistantMessage]);

			// Update claims if any were extracted inline (coaching sessions)
			if (response.extractedClaims?.length > 0) {
				const newIds = new Set(response.extractedClaims.map((c: Claim) => c.id));
				for (const id of newIds) sessionOwnedClaimIds.current.add(id);
				// All claims auto-approved — confidence persists as metadata, not a gate
				setApprovedClaims(prev => [...prev, ...response.extractedClaims]);
				setRecentClaimIds(newIds);
				setTimeout(() => setRecentClaimIds(new Set()), 4000);
			}

			// Two-pass extraction: show notification instead of polling.
			// Claims are extracted async and saved to D1 — they appear in the Record
			// on next page load or session completion. No real-time sidebar updates.
			if (response.extractionPending && !response.sessionCompleted) {
				setExtractionNotice('Claims are being added to your Record');
			}

			// Update contract from response
			if (response.contract) {
				setContract(response.contract);
			}

			// Handle session completed (backstop or wrap_up auto-complete)
			if (response.sessionCompleted) {
				setSession(prev => prev ? { ...prev, status: 'completed' } : prev);
				if (response.sessionSummary) {
					setSessionSummary(response.sessionSummary);
				}
				// Refresh compiled view on completion — extraction has had time to finish
				setExtractionNotice(null);
				setTimeout(async () => {
					try {
						const viewToken = await getToken();
						if (viewToken) {
							const view = await api.getProfileView(viewToken);
							setCompiledView(view);
						}
					} catch { /* non-critical */ }
				}, 3000); // Brief delay for final extraction to complete
			}

			// Check profile data readiness
			if (response.profileDataReady && !profileDataReady) {
				setBriefReady(true);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to send message');
			// Remove optimistic message on error
			if (optimisticMessage) {
				setMessages(prev => prev.filter(m => m !== optimisticMessage));
			}
		} finally {
			setSending(false);
		}
	}

	async function sendMessage(e: Event) {
		e.preventDefault();
		if (!input.trim()) return;

		let userMessage = input.trim();
		setInput('');

		// Reset textarea height
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
		}

		// Show user message immediately — before any API calls
		const optimisticMessage: Message = {
			role: 'user',
			content: userMessage,
			timestamp: Date.now(),
		};
		setMessages(prev => [...prev, optimisticMessage]);
		setSending(true);

		// If no focus selected yet (extraction session), auto-set guided focus first
		// Skip displaying the opening — the agent's response to the user's typed message
		// serves as the natural conversation start. Opening is still saved to DB for transcript.
		if (session?.type === 'canonical' && !sessionFocus && sessionId) {
			try {
				const token = await getToken();
				if (!token) { setSending(false); return; }
				const result = await api.setSessionFocus(token, sessionId, { type: 'guided' });
				setSessionFocus(result.sessionFocus);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to start session');
				// Remove optimistic message on error
				setMessages(prev => prev.filter(m => m !== optimisticMessage));
				setSending(false);
				return;
			}
		}

		// Prepend seed URLs to first message if present
		if (seedUrls.trim() && messages.length <= 2) {
			userMessage = `${userMessage}\n\n${seedUrls.trim()}`;
			setSeedUrls('');
		}

		await doSendMessage(userMessage, true);
	}

	async function handleClaimAction(claimId: string, action: 'approve' | 'reject', editedValue?: unknown) {
		try {
			const token = await getToken();
			if (!token) return;

			await api.claimAction(token, claimId, { action, editedValue });

			// Update local state
			const claim = pendingClaims.find(c => c.id === claimId);
			if (claim) {
				setPendingClaims(prev => prev.filter(c => c.id !== claimId));
				if (action === 'approve') {
					setApprovedClaims(prev => [...prev, { ...claim, ...(editedValue !== undefined ? { value: editedValue } : {}) }]);
				}
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to process claim');
		}
	}

	async function handleBulkClaimAction(action: 'approve' | 'reject', claimIds: string[]) {
		try {
			const token = await getToken();
			if (!token) return;
			await api.bulkClaimAction(token, { action, claimIds });
			// Update local state
			const idSet = new Set(claimIds);
			const affected = pendingClaims.filter(c => idSet.has(c.id));
			setPendingClaims(prev => prev.filter(c => !idSet.has(c.id)));
			if (action === 'approve') {
				setApprovedClaims(prev => [...prev, ...affected]);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to process claims');
		}
	}

	async function handleAbandonSession() {
		if (completing || !sessionId) return;
		setCompleting(true);
		try {
			const token = await getToken();
			if (!token) return;
			await api.abandonSession(token, sessionId);
			route('/dashboard');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to end session');
			setCompleting(false);
		}
	}

	async function handleCompleteSession() {
		if (completing || !sessionId) return;
		setCompleting(true);
		setError(null);
		try {
			const token = await getToken();
			if (!token) return;
			const result = await api.completeSession(token, sessionId);
			// Add a visible completion message in the chat
			const completionMessage: Message = {
				role: 'assistant',
				content: "Session complete. Here's a summary of what we covered.",
				timestamp: Date.now(),
			};
			setMessages(prev => [...prev, completionMessage]);
			setSession(prev => prev ? { ...prev, status: 'completed' } : prev);
			if (result.summary) {
				setSessionSummary(result.summary);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to complete session');
		} finally {
			setCompleting(false);
		}
	}

	async function handleFocusSelected(focus: SessionFocus) {
		if (settingFocus || !sessionId) return;

		setSettingFocus(true);
		setError(null);

		try {
			const token = await getToken();
			if (!token) return;

			const result = await api.setSessionFocus(token, sessionId, focus);
			setSessionFocus(result.sessionFocus);

			// Add the opening message from the server
			const openingMessage: Message = {
				role: 'assistant',
				content: result.opening,
				timestamp: Date.now(),
			};
			setMessages([openingMessage]);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to set focus');
		} finally {
			setSettingFocus(false);
		}
	}

	async function handleReorganize(target: string, targetSection: string) {
		if (!sessionId) return;

		const token = await getToken();
		if (!token) return;

		if (!sessionFocus) {
			// No focus set yet — use current session with refine focus
			setSettingFocus(true);
			try {
				const result = await api.setSessionFocus(token, sessionId, {
					type: 'refine',
					target,
					targetSection,
				});
				setSessionFocus(result.sessionFocus);
				const openingMessage: Message = {
					role: 'assistant',
					content: result.opening,
					timestamp: Date.now(),
				};
				setMessages([openingMessage]);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to set focus');
			} finally {
				setSettingFocus(false);
			}
		} else {
			// Session already has focus — start a new session
			try {
				const result = await api.startInterview(token, { type: 'canonical' });
				await api.setSessionFocus(token, result.sessionId, { type: 'refine', target, targetSection });
				route(`/interview/${result.sessionId}`);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to start session');
			}
		}
	}

	if (loading) {
		return (
			<div class="page interview-page">
				<header class="interview-header">
					<div class="header-left">
						<a href="/dashboard" class="back-link" aria-label="Back to dashboard" title="Dashboard">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
								<path d="M15 18l-6-6 6-6" />
							</svg>
						</a>
						<span class="header-title">Interview Session</span>
					</div>
					<div class="header-right">
						<UserButton afterSignOutUrl="/" />
					</div>
				</header>
				<div class="interview-layout">
					<main class="chat-container">
						<SkeletonStyles />
						<InterviewSkeleton />
					</main>
				</div>
				<style>{interviewStyles}</style>
			</div>
		);
	}

	return (
		<div class="page interview-page">
			<header class="interview-header">
				<div class="header-left">
					<a href="/dashboard" class="back-link" aria-label="Back to dashboard" title="Dashboard">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M15 18l-6-6 6-6" />
						</svg>
					</a>
					<span class="header-title">
						{session?.mode === 'private' ? 'Private Reflection' : 'Interview Session'}
					</span>
					{session?.status === 'in_progress' ? (
						<span class="status-dot status-dot--in-progress" title="In progress" aria-label="In progress" />
					) : session?.status === 'completed' ? (
						<span class="status-dot status-dot--complete" title="Complete" aria-label="Complete" />
					) : null}
				</div>
				<div class="header-right">
					<button
						class="btn btn-ghost btn-sm claims-toggle"
						onClick={() => setShowClaims(!showClaims)}
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
						</svg>
						<span class="claims-toggle-text">{showClaims ? 'Hide' : 'Show'} Record</span>
						{(() => {
							const sessionPending = session?.status === 'completed'
								? pendingClaims.filter(c => sessionOwnedClaimIds.current.has(c.id))
								: pendingClaims;
							return sessionPending.length > 0 && (
								<span class="badge badge-warning">{sessionPending.length}</span>
							);
						})()}
					</button>
					{session?.status === 'in_progress' && (
						<div class="session-end-actions">
							<button
								class="btn btn-secondary btn-sm end-session-btn"
								onClick={handleCompleteSession}
								disabled={completing}
							>
								{completing ? 'Ending...' : 'End Session'}
							</button>
							<button
								class="btn btn-ghost btn-sm abandon-session-btn"
								onClick={handleAbandonSession}
								disabled={completing}
								title="Discard this session and return to dashboard"
							>
								Discard
							</button>
						</div>
					)}
					<UserButton afterSignOutUrl="/" />
				</div>
			</header>

			<div class="interview-layout">
				<main class="chat-container">
					{showReview && (session?.type === 'canonical' || session?.type === 'manual_extraction') ? (
						<ClaimReview
							pendingClaims={pendingClaims}
							approvedClaims={approvedClaims}
							onClaimAction={handleClaimAction}
							onBulkAction={handleBulkClaimAction}
							onBack={() => setShowReview(false)}
						/>
					) : (
					<>
					{session?.status === 'in_progress' && contract && (
						<SessionProgress contract={contract} mode={session?.mode} />
					)}
					<div class="chat-messages">
						{messages.length === 0 && !sending ? (
							session?.mode === 'private' ? (
								<div class="chat-welcome chat-welcome--private">
									<div class="chat-welcome-icon">
										<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
											<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
										</svg>
									</div>
									<h3 class="chat-welcome-title">Private reflection</h3>
									<p class="chat-welcome-text">
										A space to think. Nothing here enters your record or your profile — transcript stays with you.
									</p>
									<div class="chat-prompts">
										<p class="chat-prompts-label">You could start with:</p>
										<div class="chat-prompt-buttons">
											<button
												class="chat-prompt-btn"
												onClick={() => {
													setInput("I just had an interview I want to think through. ");
													textareaRef.current?.focus();
												}}
											>
												An interview I want to process
											</button>
											<button
												class="chat-prompt-btn"
												onClick={() => {
													setInput("Something's been on my mind at work. ");
													textareaRef.current?.focus();
												}}
											>
												Something happening at work
											</button>
											<button
												class="chat-prompt-btn"
												onClick={() => {
													setInput("I'm weighing a career decision. ");
													textareaRef.current?.focus();
												}}
											>
												A career decision I'm weighing
											</button>
										</div>
									</div>
								</div>
							) : session?.type === 'canonical' && !sessionFocus && gaps.remaining.length > 0 ? (
								<SessionFocusPicker
									gaps={gaps.remaining}
									onFocusSelected={handleFocusSelected}
									loading={settingFocus}
									seedUrls={seedUrls}
									onSeedUrlsChange={setSeedUrls}
								/>
							) : (
								<div class="chat-welcome">
									<div class="chat-welcome-icon">
										<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
											<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
										</svg>
									</div>
									<h3 class="chat-welcome-title">Let's build your professional record</h3>
									<p class="chat-welcome-text">
										I'll ask you questions about your work experience, skills, and achievements to build your professional narrative.
									</p>
									<div class="chat-prompts">
										<p class="chat-prompts-label">Try starting with:</p>
										<div class="chat-prompt-buttons">
											<button
												class="chat-prompt-btn"
												onClick={() => {
													setInput("I'm a product designer with 8 years of experience");
													textareaRef.current?.focus();
												}}
											>
												"I'm a [role] with [X] years of experience"
											</button>
											<button
												class="chat-prompt-btn"
												onClick={() => {
													setInput("I currently work at [Company] as a [Title]");
													textareaRef.current?.focus();
												}}
											>
												"I currently work at [Company] as a [Title]"
											</button>
											<button
												class="chat-prompt-btn"
												onClick={() => {
													setInput("Here's my background: ");
													textareaRef.current?.focus();
												}}
											>
												Share your background in your own words
											</button>
										</div>
									</div>
								</div>
							)
						) : (
							<>
								{messages.map((msg, i) => (
									<div key={i} class={`chat-message chat-message--${msg.role}`}>
										{msg.role === 'assistant' && (
											<div class="message-avatar">
												<img src="/pxtxt-avatar.svg" alt="" width="24" height="24" />
											</div>
										)}
										<div class="message-content">
											{msg.content
												.split('\n')
												.map(line => line
													.replace(/\[CLAIM:[^\]]*\]/g, '')
													.replace(/\.?[a-z_]+(?:\[\d+\])?\.?[a-z_]*\s*\|[^|]*\|\s*confidence:\s*(?:stated|inferred)\]/g, '')
													.trim()
												)
												.filter(line => line.length > 0)
												.map((line, j) => (
												<p key={j} dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(line) }} />
											))}
										</div>
									</div>
								))}
							</>
						)}
						{sending && (
							<div class="chat-message chat-message--assistant chat-message--typing">
								<div class="message-avatar">
									<img src="/pxtxt-avatar.svg" alt="" width="24" height="24" />
								</div>
								<div class="message-content typing">
									<span></span><span></span><span></span>
								</div>
							</div>
						)}
						{sessionSummary && session?.status === 'completed' && (
							<div class="session-summary-card">
								<div class="session-summary-card__header">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
										<path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
										<polyline points="22 4 12 14.01 9 11.01" />
									</svg>
									<span>Session Complete</span>
								</div>
								<p class="session-summary-card__objective">{sessionSummary.objective}</p>
								{sessionSummary.covered.length > 0 && (
									<div class="session-summary-card__section">
										<span class="session-summary-card__label">Covered</span>
										<ul>{sessionSummary.covered.map((item, i) => <li key={i}>{item}</li>)}</ul>
									</div>
								)}
								{sessionSummary.remaining.length > 0 && (
									<div class="session-summary-card__section">
										<span class="session-summary-card__label">Still to explore</span>
										<ul>{sessionSummary.remaining.map((item, i) => <li key={i}>{item}</li>)}</ul>
									</div>
								)}
								{sessionSummary.suggestedNext && (
									<div class="session-summary-card__next">
										{sessionSummary.suggestedNext}
									</div>
								)}
								{(() => {
									const sessionPending = session?.status === 'completed'
										? pendingClaims.filter(c => sessionOwnedClaimIds.current.has(c.id))
										: pendingClaims;
									return sessionPending.length > 0 && (session?.type === 'canonical' || session?.type === 'manual_extraction') && (
										<button
											class="btn btn-primary btn-sm"
											style={{ marginTop: '0.75rem' }}
											onClick={() => setShowReview(true)}
										>
											Review {sessionPending.length} Pending Claims
										</button>
									);
								})()}
							</div>
						)}
						<div ref={messagesEndRef} />
					</div>

					{contract && (contract.phase === 'wrap_up' || contract.phase === 'grace') && session?.status === 'in_progress' && (
						<div class="session-complete-banner">
							<span class="session-complete-banner__text">
								{contract.phase === 'grace' ? 'Follow-up time remaining' : 'Session wrapping up'}
							</span>
							<button
								class="btn btn-primary btn-sm"
								onClick={handleCompleteSession}
								disabled={completing}
							>
								{completing ? 'Completing...' : 'Complete Session'}
							</button>
						</div>
					)}

					{profileDataReady && !dataBannerDismissed && session?.status === 'in_progress' && session?.mode !== 'private' && (
						<div class="data-ready-banner">
							<div class="data-ready-content">
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
									<polyline points="22 4 12 14.01 9 11.01" />
								</svg>
								<span>Your profile data is ready. Generate your narrative from the dashboard.</span>
							</div>
							<div class="data-ready-actions">
								<a href="/dashboard" class="btn btn-primary btn-sm">
									Dashboard
								</a>
								<button class="btn btn-ghost btn-sm" onClick={() => setBriefDismissed(true)}>
									Keep Going
								</button>
							</div>
						</div>
					)}

					{error && (
						<div class="chat-error">
							<span>{error}</span>
							<button onClick={() => setError(null)}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<path d="M18 6L6 18M6 6l12 12" />
								</svg>
							</button>
						</div>
					)}

					{session?.status === 'in_progress' && session?.mode === 'private' && (
						<div class="chat-scope-notice chat-scope-notice--private">
							<span class="chat-scope-notice__text">
								PRIVATE — nothing from this session enters your record or your profile. Transcript stays with you.
							</span>
						</div>
					)}

					{session?.status === 'in_progress' && session?.mode !== 'private' && (
						<div class="chat-scope-notice">
							<span class="chat-scope-notice__text">
								This session builds your record. Private reflection — a separate space for interviews, workplace dynamics, career decisions — is here if you need it.
							</span>
						</div>
					)}

					<div class="chat-input" ref={chatInputRef}>
						<div
							class="chat-input__resize"
							onMouseDown={onResizeStart}
							onTouchStart={onResizeStart}
						>
							<div class="chat-input__resize-handle" />
						</div>
						<form class="chat-input__wrapper" onSubmit={sendMessage}>
							<textarea
								ref={textareaRef}
								class="chat-input__field"
								value={input}
								onInput={(e) => {
									setInput((e.target as HTMLTextAreaElement).value);
									autoResizeTextarea();
								}}
								onPaste={() => setTimeout(autoResizeTextarea, 0)}
								onKeyDown={handleKeyDown}
								placeholder={
									session?.mode === 'private' ? "What's on your mind?"
									: "Share your professional experience..."
								}
								disabled={sending || session?.status !== 'in_progress'}
								rows={1}
							/>
							<button
								type="submit"
								class="chat-input__send"
								disabled={!input.trim() || sending || session?.status !== 'in_progress'}
							>
								<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
								</svg>
							</button>
						</form>
						<p class="chat-input__hint">Press Enter to send, Shift+Enter for new line</p>
					</div>
					</>
					)}
				</main>

				{showClaims && (
					<aside class="claims-sidebar claims-sidebar--desktop">
						<div class="sidebar-profile-link">
							<a href="/profile" class="t-caption">View full record</a>
						</div>
						{extractionNotice && (
							<div class="extraction-notice">
								<span class="extraction-notice__dot" />
								<span class="extraction-notice__text">{extractionNotice}</span>
							</div>
						)}
						{compiledView ? (
							<ProfileNotes
								compiledView={compiledView}
								pendingClaims={pendingClaims}
								onClaimAction={handleClaimAction}
								sessionClaimIds={session?.status === 'completed' ? sessionClaimIds : undefined}
								recentClaimIds={recentClaimIds}
								onReorganize={session?.status !== 'completed' ? handleReorganize : undefined}
							/>
						) : null}
					</aside>
				)}
			</div>

			{/* Mobile Claims Drawer */}
			{showClaims && (
				<div class="claims-drawer-overlay" onClick={() => setShowClaims(false)}>
					<aside class="claims-drawer" onClick={(e) => e.stopPropagation()}>
						<div class="claims-drawer-header">
							<span class="claims-drawer-title">Record</span>
							<button class="claims-drawer-close" onClick={() => setShowClaims(false)}>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<path d="M18 6L6 18M6 6l12 12" />
								</svg>
							</button>
						</div>
						{compiledView ? (
							<ProfileNotes
								compiledView={compiledView}
								pendingClaims={pendingClaims}
								onClaimAction={handleClaimAction}
								sessionClaimIds={session?.status === 'completed' ? sessionClaimIds : undefined}
								recentClaimIds={recentClaimIds}
								onReorganize={session?.status !== 'completed' ? handleReorganize : undefined}
							/>
						) : null}
					</aside>
				</div>
			)}

			<style>{interviewStyles}</style>
		</div>
	);
}

const interviewStyles = `
	.interview-page {
		display: flex;
		flex-direction: column;
		height: 100vh;
		overflow: hidden;
	}

	.interview-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.75rem 3rem;
		border-bottom: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		flex-shrink: 0;
	}

	.header-left {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	.back-link {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		margin-left: -0.375rem;
		color: var(--color-text-muted);
		text-decoration: none;
		border-radius: 4px;
		transition: color 150ms, background 150ms;
	}

	.back-link:hover {
		color: var(--color-text);
		background: var(--color-bg);
	}

	.header-title {
		font-family: var(--font-display);
		font-weight: 300;
		color: var(--color-text);
	}

	.status-dot {
		display: inline-block;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.status-dot--in-progress {
		background: var(--color-warning, #f5a623);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-warning, #f5a623) 18%, transparent);
	}

	.status-dot--complete {
		background: var(--color-success, #22c55e);
	}

	.header-right {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.interview-layout {
		display: flex;
		flex: 1;
		overflow: hidden;
	}

	.chat-container {
		flex: 1;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background: var(--color-bg);
	}

	.chat-messages {
		flex: 1;
		overflow-y: auto;
		padding: 1.5rem;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
		scrollbar-gutter: stable;
	}

	.chat-messages::before {
		content: '';
		margin-top: auto;
	}

	.chat-message {
		max-width: 85%;
		display: flex;
		gap: 6px;
		align-items: flex-end;
		animation: slide-up 200ms var(--ease-out-expo);
	}

	.chat-message--user {
		align-self: flex-end;
		flex-direction: row-reverse;
	}

	.chat-message--assistant {
		align-self: flex-start;
	}

	.message-avatar {
		width: 24px;
		height: 24px;
		flex-shrink: 0;
	}

	.message-avatar img {
		display: block;
		width: 24px;
		height: 24px;
	}

	.chat-message--user .message-avatar {
		background: var(--color-primary);
		color: #fff;
	}

	.message-content {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 12px;
		padding: 0.75rem 1rem;
		font-size: 0.9375rem;
		line-height: 1.5;
		overflow-wrap: break-word;
		word-break: break-word;
	}

	.message-content p {
		margin: 0;
	}

	.message-content p + p {
		margin-top: 0.5rem;
	}

	.chat-message--user .message-content {
		background: var(--color-primary);
		border-color: var(--color-primary);
		color: #fff;
		border-bottom-right-radius: 4px;
	}

	.chat-message--assistant .message-content {
		border-bottom-left-radius: 4px;
	}

	.typing {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 0.875rem 1rem;
	}

	.typing span {
		width: 6px;
		height: 6px;
		background: var(--color-text-muted);
		border-radius: 50%;
		animation: chat-typing 1.4s infinite;
	}

	.typing span:nth-child(2) {
		animation-delay: 0.2s;
	}

	.typing span:nth-child(3) {
		animation-delay: 0.4s;
	}

	@keyframes chat-typing {
		0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
		30% { opacity: 1; transform: translateY(-3px); }
	}

	.chat-error {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.75rem 1rem;
		margin: 0 1.5rem 1rem;
		background: rgba(222, 46, 33, 0.1);
		border: 1px solid var(--color-error);
		border-radius: var(--radius-md);
		color: var(--color-error);
		font-size: 0.875rem;
	}

	.chat-error button {
		background: none;
		border: none;
		color: inherit;
		cursor: pointer;
		padding: 4px;
		display: flex;
		opacity: 0.7;
		transition: opacity 150ms;
	}

	.chat-error button:hover {
		opacity: 1;
	}

	.chat-input {
		padding: 0 1.5rem 1rem;
		border-top: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		flex-shrink: 0;
		display: flex;
		flex-direction: column;
		min-height: 100px;
		overflow: hidden;
	}

	.chat-input.is-dragging {
		transition: none;
		user-select: none;
	}

	.chat-input__resize {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 20px;
		cursor: ns-resize;
		flex-shrink: 0;
		user-select: none;
		-webkit-user-select: none;
	}

	.chat-input__resize-handle {
		width: 40px;
		height: 4px;
		background: var(--color-border);
		border-radius: 2px;
		transition: background 150ms;
	}

	.chat-input__resize:hover .chat-input__resize-handle,
	.chat-input__resize:active .chat-input__resize-handle {
		background: var(--color-text-muted);
	}

	.chat-input__hint {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		color: var(--color-text-subtle);
		margin-top: 0.5rem;
		text-align: right;
	}

	.chat-scope-notice {
		padding: 0.625rem 1rem;
		border-top: 1px solid var(--color-border);
		background: var(--color-bg-subtle);
		text-align: center;
	}

	.chat-scope-notice--private {
		background: var(--color-bg-emphasis, var(--color-bg-subtle));
		border-top-color: var(--color-primary, var(--color-border));
	}

	.chat-scope-notice--private .chat-scope-notice__text {
		color: var(--color-text, var(--color-text-secondary));
		font-weight: 500;
		letter-spacing: 0.02em;
	}

	.chat-scope-notice__text {
		font-size: 0.8125rem;
		color: var(--color-text-secondary);
		line-height: 1.4;
	}

	.chat-input__wrapper {
		display: flex;
		align-items: flex-end;
		gap: 0.75rem;
		flex: 1;
		min-height: 0;
	}

	.chat-input__field {
		flex: 1;
		padding: 0.625rem 0;
		background: transparent;
		border: none;
		font-family: var(--font-body);
		font-size: 0.9375rem;
		color: var(--color-text);
		outline: none;
		resize: none;
		min-height: 1.5em;
		line-height: 1.5;
		overflow-y: auto;
	}

	.chat-input__field::placeholder {
		color: var(--color-text-muted);
	}

	.chat-input__field:disabled {
		opacity: 0.5;
	}

	.chat-input__send {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2.5rem;
		height: 2.5rem;
		padding: 0;
		background: var(--color-primary);
		border: none;
		border-radius: var(--radius-md);
		color: #fff;
		cursor: pointer;
		transition: background 150ms, transform 150ms;
		flex-shrink: 0;
	}

	.chat-input__send:hover:not(:disabled) {
		background: var(--color-primary-hover);
		transform: scale(1.02);
	}

	.chat-input__send:disabled {
		background: var(--color-text-muted);
		cursor: not-allowed;
	}

	.claims-sidebar {
		width: 380px;
		border-left: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
	}

	.extraction-notice {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 1rem;
		background: var(--color-bg-subtle);
		border-bottom: 1px solid var(--color-border);
		flex-shrink: 0;
	}

	.extraction-notice__dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--color-primary);
		animation: pulse-dot 2s ease-in-out infinite;
	}

	@keyframes pulse-dot {
		0%, 100% { opacity: 0.4; }
		50% { opacity: 1; }
	}

	.extraction-notice__text {
		font-size: 0.75rem;
		color: var(--color-text-subtle);
	}

	.sidebar-profile-link {
		padding: 0.5rem 1rem;
		border-bottom: 1px solid var(--color-border);
		flex-shrink: 0;
		text-align: right;
	}

	.sidebar-profile-link a {
		color: var(--color-text-subtle);
		text-decoration: none;
	}

	.sidebar-profile-link a:hover {
		color: var(--color-text);
	}

	/* Sidebar tabs */
	.sidebar-tabs {
		display: flex;
		border-bottom: 1px solid var(--color-border);
		padding: 0 12px;
		flex-shrink: 0;
	}

	.sidebar-tab {
		flex: 1;
		padding: 10px 8px;
		font-size: 13px;
		font-weight: 500;
		color: var(--color-text-muted);
		background: none;
		border: none;
		border-bottom: 2px solid transparent;
		cursor: pointer;
		transition: color 150ms, border-color 150ms;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
	}

	.sidebar-tab:hover {
		color: var(--color-text-primary);
	}

	.sidebar-tab--active {
		color: var(--color-text-primary);
		border-bottom-color: var(--color-accent, #2563eb);
	}

	.sidebar-tab-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 18px;
		height: 18px;
		padding: 0 5px;
		border-radius: 9px;
		font-size: 11px;
		font-weight: 600;
		background: var(--color-accent, #2563eb);
		color: white;
	}

	.sidebar-tabs--mobile {
		flex: 1;
		border-bottom: none;
		padding: 0;
	}

	/* Desktop sidebar */
	@media (max-width: 1024px) {
		.claims-sidebar--desktop {
			display: none;
		}
	}

	@media (min-width: 1025px) {
		.claims-drawer-overlay {
			display: none;
		}
	}

	/* Mobile drawer */
	.claims-drawer-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		z-index: 200;
		animation: fade-in 200ms ease;
	}

	.claims-drawer {
		position: fixed;
		right: 0;
		top: 0;
		bottom: 0;
		width: 100%;
		max-width: 380px;
		background: var(--color-bg-elevated);
		display: flex;
		flex-direction: column;
		overflow-y: auto;
		animation: slide-in-right 300ms var(--ease-out-expo);
		z-index: 201;
	}

	.claims-drawer-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1rem;
		border-bottom: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		position: sticky;
		top: 0;
		z-index: 10;
	}

	.claims-drawer-close {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2rem;
		height: 2rem;
		padding: 0;
		background: transparent;
		border: none;
		border-radius: var(--radius-md);
		color: var(--color-text-muted);
		cursor: pointer;
		transition: background 150ms, color 150ms;
	}

	.claims-drawer-close:hover {
		background: var(--color-bg-hover);
		color: var(--color-text);
	}

	@keyframes slide-in-right {
		from {
			transform: translateX(100%);
		}
		to {
			transform: translateX(0);
		}
	}

	@media (max-width: 767px) {
		.interview-header {
			padding: 0.75rem 1.25rem;
		}

		.header-title {
			display: none;
		}

		.chat-messages {
			padding: 1rem;
		}

		.chat-input {
			padding: 0.75rem 1rem;
		}

		.chat-error {
			margin: 0 1rem 0.75rem;
		}

		.claims-drawer {
			max-width: 100%;
		}
	}

	.session-end-actions {
		display: flex;
		gap: 0.25rem;
	}

	.end-session-btn {
		color: var(--color-text-muted);
		font-size: 0.75rem;
	}

	.end-session-btn:hover {
		color: var(--color-error, #de2e21);
	}

	.abandon-session-btn {
		color: var(--color-text-subtle);
		font-size: 0.6875rem;
	}

	.abandon-session-btn:hover {
		color: var(--color-text-muted);
	}

	@media (max-width: 480px) {
		.claims-toggle-text {
			display: none;
		}

		.chat-message {
			max-width: 90%;
		}
	}

	/* Welcome state styles */
	.chat-welcome {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: 3rem 2rem;
		max-width: 480px;
		margin: auto;
	}

	.chat-welcome-icon {
		width: 64px;
		height: 64px;
		border-radius: var(--radius-full);
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		display: flex;
		align-items: center;
		justify-content: center;
		color: var(--color-text-muted);
		margin-bottom: 1.5rem;
	}

	.chat-welcome-title {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 0.75rem;
	}

	.chat-welcome-text {
		font-size: 0.9375rem;
		color: var(--color-text-muted);
		line-height: 1.5;
		margin: 0 0 2rem;
	}

	.chat-prompts {
		width: 100%;
	}

	.chat-prompts-label {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-subtle);
		margin: 0 0 0.75rem;
	}

	.chat-prompt-buttons {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.chat-prompt-btn {
		display: block;
		width: 100%;
		padding: 0.75rem 1rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		font-family: var(--font-body);
		font-size: 0.875rem;
		color: var(--color-text);
		text-align: left;
		cursor: pointer;
		transition: border-color 200ms var(--ease-out-quart);
	}

	.chat-prompt-btn:hover {
		border-color: var(--color-text);
	}

	.opportunity-setup {
		width: 100%;
		max-width: 480px;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.opportunity-tabs {
		display: flex;
		gap: 0;
		border: 1px solid var(--color-border);
		border-radius: 2px;
		overflow: hidden;
	}

	.opportunity-tab {
		flex: 1;
		padding: 0.5rem 1rem;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-muted);
		background: var(--color-bg);
		border: none;
		border-right: 1px solid var(--color-border);
		cursor: pointer;
		transition: background 150ms, color 150ms;
	}

	.opportunity-tab:last-child {
		border-right: none;
	}

	.opportunity-tab.active {
		background: var(--color-bg-elevated);
		color: var(--color-text);
	}

	.opportunity-input {
		width: 100%;
		padding: 0.75rem 1rem;
		font-family: var(--font-body);
		font-size: 0.875rem;
		color: var(--color-text);
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		box-sizing: border-box;
	}

	.opportunity-input:focus {
		outline: none;
		border-color: var(--color-text);
	}

	.opportunity-textarea {
		width: 100%;
		padding: 0.75rem 1rem;
		font-family: var(--font-body);
		font-size: 0.875rem;
		color: var(--color-text);
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		resize: vertical;
		min-height: 120px;
		box-sizing: border-box;
	}

	.opportunity-textarea:focus {
		outline: none;
		border-color: var(--color-text);
	}

	.opportunity-submit {
		align-self: flex-end;
	}

	@media (max-width: 480px) {
		.chat-welcome {
			padding: 2rem 1rem;
		}
	}

	/* Brief-ready banner */
	.data-ready-banner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.75rem 1.5rem;
		margin: 0;
		background: var(--color-bg-elevated, #f8f9fa);
		border-top: 1px solid var(--color-primary, #3b82f6);
		flex-shrink: 0;
		position: relative;
		z-index: 1;
		animation: slide-up 200ms var(--ease-out-expo);
	}

	.data-ready-content {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.875rem;
		color: var(--color-text);
	}

	.data-ready-content svg {
		color: var(--color-primary, #3b82f6);
		flex-shrink: 0;
	}

	.data-ready-actions {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-shrink: 0;
	}

	@media (max-width: 600px) {
		.data-ready-banner {
			flex-direction: column;
			align-items: stretch;
			padding: 0.75rem 1rem;
		}

		.data-ready-actions {
			justify-content: flex-end;
		}
	}

	/* Session complete banner */
	.session-complete-banner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.625rem 1.5rem;
		background: rgba(34, 197, 94, 0.06);
		border-top: 1px solid var(--color-success, #22c55e);
		flex-shrink: 0;
		animation: slide-up 200ms var(--ease-out-expo);
	}

	.session-complete-banner__text {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		color: var(--color-success, #22c55e);
		font-weight: 500;
	}

	/* Session summary card */
	.session-summary-card {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		padding: 1rem 1.25rem;
		margin-bottom: 0.75rem;
		animation: slide-up 200ms var(--ease-out-expo);
	}

	.session-summary-card__header {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-success, #22c55e);
		margin-bottom: 0.625rem;
	}

	.session-summary-card__header svg {
		color: var(--color-success, #22c55e);
	}

	.session-summary-card__objective {
		font-size: 0.9375rem;
		color: var(--color-text);
		line-height: 1.5;
		margin: 0 0 0.75rem;
	}

	.session-summary-card__section {
		margin-bottom: 0.5rem;
	}

	.session-summary-card__label {
		display: block;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-muted);
		margin-bottom: 0.25rem;
	}

	.session-summary-card__section ul {
		margin: 0;
		padding-left: 1.25rem;
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		line-height: 1.6;
	}

	.session-summary-card__next {
		margin-top: 0.75rem;
		padding-top: 0.625rem;
		border-top: 1px solid var(--color-border);
		font-size: 0.8125rem;
		color: var(--color-text-muted);
		font-style: italic;
	}

	@media (max-width: 600px) {
		.session-complete-banner {
			padding: 0.625rem 1rem;
		}
	}
`;

